import React, { useState } from "react";
import { Copy, Check } from "lucide-react";
import { Badge } from "@cms/ui/components/badge";
import { Button } from "@cms/ui/components/button";
import { STATUS_BADGE, recordHint } from "../lib/domain-status";
import type { DomainRecordState } from "@cms/types";

// Setup-checklist rows for one custom hostname (the root domain or its www
// sibling): the TXT ownership record and the CNAME record the registrar needs,
// each with copy-to-clipboard, plus the hostname's live status badge.

function CopyButton({ value, label }: { value: string; label: string }) {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  return (
    <Button size="sm" variant="outline" onClick={copy} title={`Copy ${label}`} className="shrink-0">
      {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
      {copied ? "Copied" : "Copy"}
    </Button>
  );
}

function DnsRecordRow({ type, host, value }: { type: string; host: string; value: string }) {
  return (
    <div className="flex items-center gap-2">
      <div className="grid min-w-0 flex-1 grid-cols-[3rem_1fr] items-baseline gap-x-3 gap-y-0.5 sm:grid-cols-[3rem_minmax(0,1fr)_minmax(0,1.4fr)]">
        <span className="text-xs font-medium text-muted-foreground">{type}</span>
        <code className="truncate rounded bg-muted px-2 py-1 font-mono text-xs" title={host}>{host}</code>
        <code className="col-start-2 truncate rounded bg-muted px-2 py-1 font-mono text-xs sm:col-start-3" title={value}>
          {value}
        </code>
      </div>
      <CopyButton value={value} label={`${type} value`} />
    </div>
  );
}

export function DomainRecordChecklist({ record }: { record: DomainRecordState }) {
  const badge = STATUS_BADGE[record.status];
  const hint = recordHint(record);
  const isApex = !record.is_www;

  return (
    <div className="rounded-lg border p-3.5">
      <div className="flex items-center justify-between gap-3">
        <p className="min-w-0 truncate font-mono text-sm font-medium">{record.hostname}</p>
        <Badge variant={badge.variant}>{badge.label}</Badge>
      </div>

      {record.status !== "active" && (
        <div className="mt-3 space-y-2">
          {record.verification_txt_name && record.verification_txt_value && (
            <DnsRecordRow
              type="TXT"
              host={record.verification_txt_name}
              value={record.verification_txt_value}
            />
          )}
          {record.cname_target && (
            <DnsRecordRow type="CNAME" host={record.hostname} value={record.cname_target} />
          )}
          {isApex && record.cname_target && (
            <p className="text-xs text-muted-foreground">
              Root domain: if the registrar doesn't allow a plain CNAME here, use its CNAME
              flattening option (often called ALIAS or ANAME).
            </p>
          )}
        </div>
      )}

      {hint && <p className="mt-2 text-xs text-warning-foreground">{hint}</p>}
    </div>
  );
}
